import React, { memo } from 'react';
import {
  Card,
  CardContent,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  Chip,
  Typography,
} from '@mui/material';
import {
  Favorite,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import SearchInputMemo from './SearchInputMemo';

interface SourcesFilterCardProps {
  // Search
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;

  // Primary filters
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  minRating: string;
  onMinRatingChange: (rating: string) => void;

  // Action buttons
  showFavorites: boolean;
  onFavoritesToggle: () => void;
  showAdvanced: boolean;
  onAdvancedToggle: () => void;

  // Advanced filters
  selectedPlatform: string;
  onPlatformChange: (platform: string) => void;
  selectedAuthor: string;
  onAuthorChange: (author: string) => void;
  dateFrom: string;
  onDateFromChange: (date: string) => void;
  dateTo: string;
  onDateToChange: (date: string) => void;

  // Privacy filters
  mySourcesFilter: string;
  onMySourcesFilterChange: (filter: string) => void;
  showOthersPublic: boolean;
  onShowOthersPublicChange: (show: boolean) => void;

  // Filter options
  allTags: string[];
  allPlatforms: string[];
  allAuthors: string[];

  // Clear filters
  onClearFilters: () => void;
  hasActiveFilters: boolean | string;

  // Navigation
  onAddSource: () => void;
}

const SourcesFilterCard = memo(({
  // Search
  searchTerm,
  onSearchChange,
  isLoading,

  // Primary filters
  selectedTags,
  onTagsChange,
  minRating,
  onMinRatingChange,

  // Action buttons
  showFavorites,
  onFavoritesToggle,
  showAdvanced,
  onAdvancedToggle,

  // Advanced filters
  selectedPlatform,
  onPlatformChange,
  selectedAuthor,
  onAuthorChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,

  // Privacy filters
  mySourcesFilter,
  onMySourcesFilterChange,
  showOthersPublic,
  onShowOthersPublicChange,

  // Filter options
  allTags,
  allPlatforms,
  allAuthors,

  // Clear filters
  onClearFilters,
  hasActiveFilters,

  // Navigation
  onAddSource,
}: SourcesFilterCardProps) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Main search input - memoized to prevent re-renders when sources update */}
        <SearchInputMemo value={searchTerm} onChange={onSearchChange} isLoading={isLoading} />

        {/* Primary filters */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
          <Autocomplete
            multiple
            options={allTags}
            value={selectedTags}
            onChange={(_, newValue) => onTagsChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder={selectedTags.length === 0 ? "Select tags..." : ""}
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />

          <FormControl fullWidth size="small">
            <InputLabel>Minimum Rating</InputLabel>
            <Select
              value={minRating}
              label="Minimum Rating"
              onChange={(e) => onMinRatingChange(e.target.value)}
            >
              <MenuItem value="">All Ratings</MenuItem>
              <MenuItem value="5">5 Stars</MenuItem>
              <MenuItem value="4">4+ Stars</MenuItem>
              <MenuItem value="3">3+ Stars</MenuItem>
              <MenuItem value="2">2+ Stars</MenuItem>
              <MenuItem value="1">1+ Star</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant={showFavorites ? "contained" : "outlined"}
            startIcon={<Favorite />}
            onClick={onFavoritesToggle}
            size="small"
          >
            {showFavorites ? 'Show All' : 'Favorites Only'}
          </Button>

          <Button
            variant="outlined"
            startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
            onClick={onAdvancedToggle}
            size="small"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
          </Button>

          {hasActiveFilters && (
            <Button
              variant="outlined"
              onClick={onClearFilters}
              size="small"
              color="secondary"
            >
              Clear All Filters
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            onClick={onAddSource}
          >
            Add Source
          </Button>
        </Box>

        {/* Advanced filters collapsible section */}
        {showAdvanced && (
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              Advanced Filters
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
              <Autocomplete
                freeSolo
                options={allPlatforms}
                value={selectedPlatform}
                onChange={(_, newValue) => onPlatformChange(newValue || '')}
                onInputChange={(_, newInputValue) => onPlatformChange(newInputValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Platform"
                    placeholder="e.g., YouTube, Wikipedia"
                    size="small"
                  />
                )}
              />

              <Autocomplete
                freeSolo
                options={allAuthors}
                value={selectedAuthor}
                onChange={(_, newValue) => onAuthorChange(newValue || '')}
                onInputChange={(_, newInputValue) => onAuthorChange(newInputValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Author"
                    placeholder="Author or creator"
                    size="small"
                  />
                )}
              />

              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Privacy filters section */}
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              Privacy Filters
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>My Sources</InputLabel>
                <Select
                  value={mySourcesFilter}
                  label="My Sources"
                  onChange={(e) => onMySourcesFilterChange(e.target.value)}
                >
                  <MenuItem value="all">All (Public + Private)</MenuItem>
                  <MenuItem value="public">Public Only</MenuItem>
                  <MenuItem value="private">Private Only</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Other Users' Sources</InputLabel>
                <Select
                  value={showOthersPublic ? 'show' : 'hide'}
                  label="Other Users' Sources"
                  onChange={(e) => onShowOthersPublicChange(e.target.value === 'show')}
                >
                  <MenuItem value="show">Show Public Sources</MenuItem>
                  <MenuItem value="hide">Hide Public Sources</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

SourcesFilterCard.displayName = 'SourcesFilterCard';

export default SourcesFilterCard;
